import { Fragment, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import {
  listDeliveries,
  resendDelivery,
  retryDelivery,
  type Delivery,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { SearchBox } from "@/components/SearchBox";
import { useConfirmFocus } from "@/components/confirmFocus";
import { formatDate, titleStatus } from "@/components/format";
import {
  EmptyState,
  ErrorBanner,
  StatusBadge,
  ToolbarButton,
  WorkspaceHeader,
} from "@/components/ui";
import { DELIVERY_STATUSES, type DeliveriesSearch } from "@/routes/deliveries";
import { ScrollArea } from "@/components/ScrollArea";
import { PagedFooter } from "@/components/Pagination";
import { pageParams } from "@/routes/paging";

const deliveriesApi = getRouteApi("/staff/deliveries");

const selectClass =
  "rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm";
const COLUMNS = 8;

export function DeliveryList() {
  const search = deliveriesApi.useSearch();
  const { batch, channel, status, q, page, page_size } = search;
  const { can } = useSession();
  const navigate = useNavigate();

  const deliveries = useQuery({
    queryKey: ["deliveries", { batch, channel, status, q, page, page_size }],
    queryFn: () => {
      const params: Parameters<typeof listDeliveries>[0] = pageParams(search);
      if (batch) params.batchId = batch;
      if (channel) params.channel = channel;
      if (status) params.status = status;
      if (q) params.q = q;
      return listDeliveries(params);
    },
  });
  const items = deliveries.data?.items ?? [];
  // Page level so it outlives the retried row, which leaves a status-filtered list on refetch.
  const [requeued, setRequeued] = useState(false);
  const requeuedRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (requeued) requeuedRef.current?.focus();
  }, [requeued]);
  useEffect(() => {
    setRequeued(false);
  }, [batch, channel, status, q, page]);

  // Any filter change starts again from the first page.
  const filter = (next: Partial<DeliveriesSearch>) =>
    void navigate({
      to: "/deliveries",
      search: { batch, channel, status, q, page_size, ...next, page: undefined },
    });

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title="Deliveries"
        detail="Email and print deliveries, newest first."
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox
          q={q}
          ariaLabel="Search deliveries"
          placeholder="Name, address or certificate number"
          onSearch={(text) => filter({ q: text || undefined })}
        />
        <select
          aria-label="Channel"
          value={channel ?? ""}
          onChange={(e) =>
            filter({
              channel: (e.target.value ||
                undefined) as DeliveriesSearch["channel"],
            })
          }
          className={selectClass}
        >
          <option value="">All channels</option>
          <option value="email">Email</option>
          <option value="print">Print</option>
        </select>
        <select
          aria-label="Status"
          value={status ?? ""}
          onChange={(e) =>
            filter({
              status: (e.target.value ||
                undefined) as DeliveriesSearch["status"],
            })
          }
          className={selectClass}
        >
          <option value="">All statuses</option>
          {DELIVERY_STATUSES.map((s) => (
            <option key={s} value={s}>
              {titleStatus(s)}
            </option>
          ))}
        </select>
        {batch ? (
          <span className="flex items-center gap-2 rounded-full bg-zinc-100 px-3 py-1 text-xs text-zinc-700">
            Batch {batch.slice(0, 8)}
            <button
              type="button"
              className="font-medium underline"
              onClick={() => filter({ batch: undefined })}
            >
              Clear
            </button>
          </span>
        ) : null}
      </div>

      <ErrorBanner error={deliveries.error} />
      {requeued ? (
        <p
          ref={requeuedRef}
          role="status"
          tabIndex={-1}
          className="text-sm font-medium text-emerald-700 outline-none"
        >
          Requeued.
        </p>
      ) : null}
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        {deliveries.isLoading ? (
          <EmptyState
            title="Loading deliveries"
            detail="Fetching deliveries."
          />
        ) : deliveries.isSuccess && items.length === 0 ? (
          <EmptyState
            title="No deliveries found"
            detail="Nothing matches these filters."
          />
        ) : (
          <ScrollArea label="Deliveries">
            <table
              aria-label="Deliveries"
              className="w-full min-w-[960px] text-left text-sm"
            >
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Recipient</th>
                  <th className="px-4 py-3 font-medium">Certificate</th>
                  <th className="px-4 py-3 font-medium">Channel</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Attempts</th>
                  <th className="px-4 py-3 font-medium">Last error</th>
                  <th className="px-4 py-3 font-medium">Updated</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((delivery) => (
                  <DeliveryRow
                    key={delivery.id}
                    delivery={delivery}
                    canReadCertificate={can("certificate.read")}
                    canSend={can("certificate.send")}
                    onAction={() => setRequeued(false)}
                    onRequeued={() => setRequeued(true)}
                  />
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
        <PagedFooter data={deliveries.data} page={page} pageSize={page_size} />
      </div>

    </section>
  );
}

function DeliveryRow({
  delivery,
  canReadCertificate,
  canSend,
  onAction,
  onRequeued,
}: {
  delivery: Delivery;
  canReadCertificate: boolean;
  canSend: boolean;
  onAction: () => void;
  onRequeued: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<"retry" | "resend" | null>(null);
  const { trigger, confirm } = useConfirmFocus(confirming !== null);
  const [done, setDone] = useState<"resend" | null>(null);
  // The original row stays "sent" after a resend; a second click would mail the recipient again.
  const [resent, setResent] = useState(false);
  const doneRef = useRef<HTMLParagraphElement>(null);
  // Retry unmounts on refetch and the confirm closes; keep focus on the outcome, not the body.
  useEffect(() => {
    if (done) doneRef.current?.focus();
  }, [done]);
  const act = useMutation({
    mutationFn: (kind: "retry" | "resend") =>
      kind === "retry"
        ? retryDelivery(delivery.id)
        : resendDelivery(delivery.id),
    onSuccess: async (_, kind) => {
      setConfirming(null);
      if (kind === "retry") onRequeued();
      else {
        setDone(kind);
        setResent(true);
      }
      await queryClient.invalidateQueries({ queryKey: ["deliveries"] });
    },
    onSettled: () => {
      submitting.current = false;
      setConfirming(null);
    },
  });
  // A second click lands before the disabled state re-renders.
  const submitting = useRef(false);
  const run = (kind: "retry" | "resend") => {
    if (submitting.current) return;
    submitting.current = true;
    act.mutate(kind);
  };

  const isEmail = delivery.channel === "email";
  const unconfirmed =
    delivery.last_error?.startsWith("send not confirmed") ?? false;
  const canRetry = canSend && isEmail && delivery.status === "failed";
  const canResend = canSend && isEmail && delivery.status === "sent";
  const name = delivery.full_name;

  const retry = () => {
    act.reset();
    setDone(null);
    onAction();
    if (unconfirmed) setConfirming("retry");
    else run("retry");
  };

  return (
    <Fragment>
      <tr className="hover:bg-zinc-50">
        <td className="px-4 py-3">
          <div className="font-medium text-zinc-950">{name}</div>
          <div className="text-xs text-zinc-500">
            {delivery.recipient ?? "No address"}
          </div>
        </td>
        <td className="px-4 py-3 text-zinc-700">
          {canReadCertificate ? (
            <Link
              to="/certificates/$certificateId"
              params={{ certificateId: delivery.certificate_id }}
              className="underline-offset-2 hover:underline"
            >
              {delivery.certificate_no}
            </Link>
          ) : (
            delivery.certificate_no
          )}
        </td>
        <td className="px-4 py-3 text-zinc-700">
          {isEmail ? "Email" : "Print"}
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={delivery.status} />
        </td>
        <td className="px-4 py-3 text-zinc-700">{delivery.attempts}</td>
        <td className="px-4 py-3 text-red-700">{delivery.last_error}</td>
        <td className="px-4 py-3 text-zinc-500">
          {formatDate(delivery.updated_at)}
        </td>
        <td className="px-4 py-3">
          {canRetry ? (
            <ToolbarButton
              ref={trigger}
              disabled={act.isPending || confirming !== null}
              onClick={retry}
            >
              Retry
            </ToolbarButton>
          ) : canResend ? (
            <ToolbarButton
              ref={trigger}
              disabled={act.isPending || confirming !== null || resent}
              onClick={() => {
                act.reset();
                setDone(null);
                onAction();
                setConfirming("resend");
              }}
            >
              Resend
            </ToolbarButton>
          ) : null}
        </td>
      </tr>
      {confirming || act.error || done ? (
        <tr>
          <td colSpan={COLUMNS} className="space-y-2 bg-zinc-50 px-4 py-3">
            {confirming ? (
              <div
                role="group"
                aria-label={`Confirm ${confirming} ${name}`}
                className="flex flex-wrap items-center gap-3 text-sm"
              >
                <p className="min-w-72 flex-1 text-zinc-700">
                  {confirming === "retry"
                    ? "This email may already have been delivered. Check with the recipient before sending it again."
                    : `${name} was already emailed. Send the certificate again?`}
                </p>
                <ToolbarButton
                  ref={confirm}
                  disabled={act.isPending}
                  onClick={() => run(confirming)}
                >
                  {confirming === "retry" ? "Retry anyway" : "Resend"}
                </ToolbarButton>
                <ToolbarButton
                  disabled={act.isPending}
                  onClick={() => setConfirming(null)}
                >
                  Cancel
                </ToolbarButton>
              </div>
            ) : null}
            {done ? (
              <p
                ref={doneRef}
                role="status"
                tabIndex={-1}
                className="text-sm font-medium text-emerald-700 outline-none"
              >
                Resend queued to {delivery.recipient ?? name}.
              </p>
            ) : null}
            <ErrorBanner error={act.error} />
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}
